import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useRegister } from '@/hooks/authHooks'
import { resolveAuth } from '@/lib/auth'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'

import AuthCard from '@/components/auth/AuthCard'
import AuthForm from '@/components/auth/AuthForm'
import SocialButtons from '@/components/auth/SocialButtons'
import ImageSection from '@/components/auth/ImageSection'

const registerSchema = z
  .object({
    name: z.string().min(2),
    email: z.string().email(),
    password: z.string().min(6),
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'كلمة المرور غير متطابقة',
    path: ['password_confirmation'],
  })

export const Route = createFileRoute('/_regular/register/')({
  beforeLoad: async ({ context: { queryClient } }) => {
    const auth = await resolveAuth(queryClient)
    if (auth.isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: RegisterPage,
})

function RegisterPage() {
  const registerMutation = useRegister()
  const [serverError, setServerError] = useState('')

  const navigate = Route.useNavigate()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
    },
  })

  const onSubmit = async (values) => {
    setServerError('')

    try {
      await registerMutation.mutateAsync(values)
      navigate({ to: '/dashboard' })
    } catch (error) {
      setServerError(error.response?.data?.message || 'فشل إنشاء الحساب')
    }
  }

  const handleSocialLogin = (provider) => {
    alert(`سيتم تفعيل التسجيل عبر ${provider} قريباً`)
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col md:flex-row">

        <div className="relative overflow-hidden order-2 md:order-1 w-full md:w-1/2 flex items-center justify-center p-6 md:p-12">

          <AuthCard
            title="إنشاء حساب جديد"
            description="أدخل بياناتك للتسجيل في المنصة"
          >

            <AuthForm
              mode="register"
              control={control}
              loading={registerMutation.isPending}
              onSubmit={handleSubmit(onSubmit)}
            />

            {/* FIELD ERRORS */}
            {Object.values(errors).length > 0 && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>
                  {Object.values(errors)[0]?.message}
                </AlertDescription>
              </Alert>
            )}

            {/* SERVER ERROR */}
            {serverError && (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <Separator className="my-6" />

            <SocialButtons onSocialClick={handleSocialLogin} />

            <div className="mt-6 text-center text-sm">
              لديك حساب بالفعل؟{' '}
              <Link to="/login" className="text-primary hover:underline">
                تسجيل الدخول
              </Link>
            </div>

            <p className="mt-6 text-xs text-center text-muted-foreground">
              بالتسجيل فإنك توافق على شروط الاستخدام وسياسة الخصوصية
            </p>

          </AuthCard>
        </div>

        <div className="order-1 md:order-2 w-full md:w-1/2 h-64 md:h-auto">
          <ImageSection />
        </div>

      </div>
    </div>
  )
}